import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, MapPin, AlertCircle, Home, ArrowLeft, Phone, User, Upload, X } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { supabase } from '../lib/supabase';
import { useTranslation } from 'react-i18next';
import { useUzbekistanData } from '../lib/hooks/useUzbekistanData';
import { useBusinessProfile } from '../lib/businessProfileContext';
import YandexMapLocationPicker from '../components/YandexMapLocationPicker';


export default function BusinessEdit() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { businessProfile: contextBusinessProfile, refreshProfile } = useBusinessProfile();
  const { regions, getDistrictsByRegion, loading: dataLoading, error: dataError } = useUzbekistanData();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [formData, setFormData] = useState({
    businessName: '',
    regionId: '',
    districtId: '',
    address: '',
    phone: '+998',
    masterName: '',
    logoUrl: '',
    latitude: '',
    longitude: ''
  });

  const selectedRegion = regions.find(r => r.id.toString() === formData.regionId);
  const availableDistricts = selectedRegion ? getDistrictsByRegion(selectedRegion.id) : [];

  useEffect(() => {
    if (contextBusinessProfile && regions.length > 0) {
      // Use separate region and city fields if available, otherwise fall back to parsing location
      let regionId = '';
      let districtId = '';
      let address = contextBusinessProfile.address || '';

      if (contextBusinessProfile.region && contextBusinessProfile.city) {
        // Use the separate region and city fields
        const region = regions.find(r => r.name_uz === contextBusinessProfile.region || r.name_ru === contextBusinessProfile.region);
        const district = region ? getDistrictsByRegion(region.id).find(d => d.name_uz === contextBusinessProfile.city || d.name_ru === contextBusinessProfile.city) : null;
        
        regionId = region?.id.toString() || '';
        districtId = district?.id.toString() || '';
      } else {
        // Fall back to parsing the location string for backward compatibility
        const locationParts = contextBusinessProfile.location.split(', ');
        address = locationParts[0] || '';
        const city = locationParts[1] || '';
        const regionName = locationParts[2] || '';

        const region = regions.find(r => r.name_uz === regionName || r.name_ru === regionName);
        const district = region ? getDistrictsByRegion(region.id).find(d => d.name_uz === city || d.name_ru === city) : null;

        regionId = region?.id.toString() || '';
        districtId = district?.id.toString() || '';
      }

      setFormData({
        businessName: contextBusinessProfile.business_name,
        regionId: regionId,
        districtId: districtId,
        address: address,
        phone: contextBusinessProfile.phone || '+998',
        masterName: contextBusinessProfile.master_name || '',
        logoUrl: contextBusinessProfile.logo_url || '',
        latitude: contextBusinessProfile.latitude?.toString() || '',
        longitude: contextBusinessProfile.longitude?.toString() || ''
      });

      // Set logo preview from database if no new file is selected
      if (contextBusinessProfile.logo_url && !logoFile) {
        setLogoPreview(contextBusinessProfile.logo_url);
      }

      setInitialLoading(false);
    }
  }, [contextBusinessProfile, regions, getDistrictsByRegion]);

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('Logo file size must be less than 5MB');
        return;
      }

      if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(file.type)) {
        setError('Logo must be a JPEG, PNG, or WebP image');
        return;
      }

      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
      setError(null);
    }
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    setFormData(prev => ({ ...prev, logoUrl: '' }));
  };

  const uploadLogo = async (): Promise<string | null> => {
    if (!logoFile || !user) return formData.logoUrl || null;

    setUploadingLogo(true);
    try {
      // Delete old logo from storage if it exists
      if (contextBusinessProfile?.logo_url) {
        try {
          // Extract path from URL: https://...supabase.co/storage/v1/object/public/business-logos/{user_id}/logo.{ext}
          const urlParts = contextBusinessProfile.logo_url.split('/business-logos/');
          if (urlParts.length > 1) {
            const oldPath = urlParts[1];
            await supabase.storage.from('business-logos').remove([oldPath]);
          }
        } catch (deleteErr) {
          console.warn('Failed to delete old logo, continuing with upload:', deleteErr);
          // Don't block upload if deletion fails
        }
      }

      // Upload new logo to user's folder
      const fileExt = logoFile.name.split('.').pop() || 'png';
      const fileName = `logo.${fileExt}`;
      const filePath = `${user.id}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('business-logos')
        .upload(filePath, logoFile, {
          upsert: true, // Replace if exists
          contentType: logoFile.type
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('business-logos')
        .getPublicUrl(filePath);

      return publicUrl;
    } catch (err) {
      console.error('Logo upload error:', err);
      throw new Error(err instanceof Error ? err.message : 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleLocationSelect = (latitude: number, longitude: number) => {
    setFormData(prev => ({
      ...prev,
      latitude: latitude.toString(),
      longitude: longitude.toString()
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      // Start with whatever is currently in formData (or null)
      let logoUrlToSave: string | null = formData.logoUrl || null;

      // Handle logo upload separately (optional)
      if (logoFile) {
        try {
          const uploadedUrl = await uploadLogo();
          if (uploadedUrl) {
            logoUrlToSave = uploadedUrl;
            setFormData(prev => ({ ...prev, logoUrl: uploadedUrl }));
          }
        } catch (logoErr) {
          console.warn('Logo upload failed, but continuing with form submission:', logoErr);
          // Don't block form submission if logo upload fails
        }
      }

      const selectedDistrict = availableDistricts.find(d => d.id.toString() === formData.districtId);
      const location = `${formData.address}, ${selectedDistrict?.name_uz || ''}, ${selectedRegion?.name_uz || ''}`;

      const { error: updateError } = await supabase
        .from('business_profiles')
        .update({
          business_name: formData.businessName,
          region: selectedRegion?.name_uz || null,
          city: selectedDistrict?.name_uz || null,
          address: formData.address,
          location: location, // Keep for backward compatibility
          phone: formData.phone,
          master_name: formData.masterName || null,
          logo_url: logoUrlToSave,
          latitude: formData.latitude ? parseFloat(formData.latitude) : null,
          longitude: formData.longitude ? parseFloat(formData.longitude) : null
        })
        .eq('user_id', user.id);

      if (updateError) throw updateError;
      
      // Refresh the business profile context
      await refreshProfile();
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update business profile');
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading || dataLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">
            {dataLoading ? 'Viloyatlar ma\'lumotlari yuklanmoqda...' : 'Biznes ma\'lumotlari yuklanmoqda...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-gray-600 mb-8 mx-4 hover:text-gray-900"
        >
          <ArrowLeft className="w-5 h-5" />
          Bosh sahifaga qaytish
        </button>
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <div className="sm:mx-auto sm:w-full sm:max-w-md">
            <h2 className="text-center text-3xl font-extrabold text-gray-900">
              Biznesni tahrirlash
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              Biznes ma'lumotlaringizni yangilang
            </p>
          </div>

          {(error || dataError) && (
            <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
              <div className="flex-1">
                <p className="text-red-700">{error || dataError}</p>
              </div>
            </div>
          )}

          {dataLoading && (
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-3">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
              <p className="text-blue-700">Viloyatlar ma'lumotlari yuklanmoqda...</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="mt-8 space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Biznes logotipi
              </label>
              <div className="flex items-center gap-4">
                {logoPreview ? (
                  <div className="relative">
                    <img
                      src={logoPreview}
                      alt="Business logo"
                      className="w-24 h-24 object-cover rounded-lg border-2 border-gray-300"
                    />
                    <button
                      type="button"
                      onClick={handleRemoveLogo}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50">
                    <Upload className="w-8 h-8 text-gray-400" />
                  </div>
                )}
                <div className="flex-1">
                  <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                    <Upload className="w-4 h-4" />
                    {logoPreview ? 'Logotipni almashtirish' : 'Logotip yuklash'}
                    <input
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/webp"
                      onChange={handleLogoChange}
                      className="hidden"
                    />
                  </label>
                  <p className="mt-1 text-xs text-gray-500">
                    PNG, JPG, WEBP (max 5MB)
                  </p>
                </div>
              </div>
            </div>

            <div>
              <label htmlFor="businessName" className="block text-sm font-medium text-gray-700">
                Biznes nomi
              </label>
              <div className="mt-1 relative">
                <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  id="businessName"
                  type="text"
                  required
                  value={formData.businessName}
                  onChange={(e) => setFormData(prev => ({ ...prev, businessName: e.target.value }))}
                  className="pl-10 pr-4 py-4 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-base"
                  placeholder="Masalan: Tech Repair Service"
                />
              </div>
            </div>

            <div>
              <label htmlFor="region" className="block text-sm font-medium text-gray-700">
                Viloyat
              </label>
              <div className="mt-1 relative">
                <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <select
                  id="region"
                  required
                  value={formData.regionId}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    regionId: e.target.value,
                    districtId: '' // Reset district when region changes
                  }))}
                  className="pl-10 pr-10 py-4 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-base"
                  disabled={dataLoading}
                >
                  <option value="">Viloyatni tanlang</option>
                  {regions.map((region) => (
                    <option key={region.id} value={region.id}>
                      {region.name_uz}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="city" className="block text-sm font-medium text-gray-700">
                Shahar/Tuman
              </label>
              <div className="mt-1 relative">
                <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <select
                  id="district"
                  required
                  value={formData.districtId}
                  onChange={(e) => setFormData(prev => ({ ...prev, districtId: e.target.value }))}
                  className="pl-10 pr-10 py-4 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-base"
                  style={{ backgroundColor: !formData.regionId ? '#f9fafb' : 'white' }}
                  disabled={!formData.regionId || dataLoading}
                >
                  <option value="">Shaharni tanlang</option>
                  {availableDistricts.map((district) => (
                    <option key={district.id} value={district.id}>
                      {district.name_uz}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                Manzil
              </label>
              <div className="mt-1 relative">
                <Home className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  id="address"
                  type="text"
                  required
                  value={formData.address}
                  onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                  className="pl-10 pr-4 py-4 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-base"
                  placeholder="Ko'cha nomi va uy raqami"
                />
              </div>
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                Telefon raqami
              </label>
              <div className="mt-1 relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  id="phone"
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={(e) => {
                    let value = e.target.value;
                    if (!value.startsWith('+998')) {
                      value = '+998' + value.replace(/^\+?998?/, '');
                    }
                    value = '+998' + value.slice(4).replace(/\D/g, '');
                    if (value.length > 13) {
                      value = value.slice(0, 13);
                    }
                    setFormData(prev => ({ ...prev, phone: value }));
                  }}
                  className="pl-10 pr-4 py-4 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-base"
                  placeholder="+998901234567"
                  maxLength={13}
                />
              </div>
            </div>

            <div>
              <label htmlFor="masterName" className="block text-sm font-medium text-gray-700">
                Master ismi (SMS uchun)
              </label>
              <div className="mt-1 relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  id="masterName"
                  type="text"
                  value={formData.masterName}
                  onChange={(e) => setFormData(prev => ({ ...prev, masterName: e.target.value }))}
                  className="pl-10 pr-4 py-4 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-base"
                  placeholder="Masalan: Qahramon"
                />
              </div>
              <p className="mt-1 text-sm text-gray-500">
                Bu ism SMS xabarlarida ko'rsatiladi
              </p>
            </div>

            {/* Interactive Map for Location Selection */}
            <div className="border-t pt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5" />
                Joylashuvni tanlang (ixtiyoriy)
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Xaritada joylashuvni tanlang yoki joriy joylashuvni oling
              </p>
              
              <YandexMapLocationPicker
                latitude={formData.latitude ? parseFloat(formData.latitude) : null}
                longitude={formData.longitude ? parseFloat(formData.longitude) : null}
                onLocationSelect={handleLocationSelect}
                height="250px"
              />

              {/* Display current coordinates */}
              {(formData.latitude || formData.longitude) && (
                <div className="mt-3 p-2.5 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span>Lat: <span className="font-mono text-gray-700">{formData.latitude ? parseFloat(formData.latitude).toFixed(6) : '—'}</span></span>
                    <span>Lng: <span className="font-mono text-gray-700">{formData.longitude ? parseFloat(formData.longitude).toFixed(6) : '—'}</span></span>
                  </div>
                </div>
              )}
            </div>
            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => navigate('/')}
                className="flex-1 py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                Bekor qilish
              </button>
              <button
                type="submit"
                disabled={loading || uploadingLogo}
                className="flex-1 py-2 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploadingLogo ? 'Logotip yuklanmoqda...' : loading ? 'Saqlanmoqda...' : 'O\'zgarishlarni saqlash'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}